import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import { Toaster } from "./components/ui/sonner";
import ClerkAuthProvider from "./components/auth/ClerkAuthProvider";
import { MeProvider, useMe } from "./lib/api";
import { PublicLayout } from "./components/site/Layout";
import Home from "./pages/public/Home";
import { HowItWorks, Services, WebsiteDevelopment, About, Faqs, Contact, Legal } from "./pages/public/Pages";
import Configurator from "./pages/configurator/Configurator";
import Dashboard from "./pages/dashboard/Dashboard";
import ProjectView from "./pages/dashboard/ProjectView";
import PreviewPage from "./pages/dashboard/PreviewPage";
import CreditsPage from "./pages/dashboard/CreditsPage";
import Admin from "./pages/admin/Admin";
import PresBuilder, { PresList } from "./pages/presentations/PresBuilder";
import PublicSite, { isCustomDomain } from "./pages/public/PublicSite";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "./lib/api";

function Protected({ children }) {
  const loc = useLocation();
  return (<>
    <Show when="signed-in">{children}</Show>
    <Show when="signed-out"><Navigate to={`/sign-in?redirect_url=${encodeURIComponent(loc.pathname)}`} replace /></Show>
  </>);
}

function StaffOnly({ children }) {
  const { me } = useMe();
  if (!me) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  if (!["admin", "staff"].includes(me.role)) return <PublicLayout><div className="max-w-xl mx-auto py-24 px-5" data-testid="admin-forbidden"><h1 className="text-3xl">Access restricted</h1><p className="mt-2 text-slate-600">This area is only for Altiora administrators.</p></div></PublicLayout>;
  return children;
}

const AuthPage = ({ children }) => (
  <PublicLayout><div className="flex items-center justify-center py-16 px-4">{children}</div></PublicLayout>
);

function DomainGate({ children }) {
  const [state, setState] = useState(window.location.pathname.startsWith("/site/") ? "slug" : isCustomDomain() ? "checking" : "app");
  useEffect(() => {
    if (state !== "checking") return;
    axios.get(`${API}/sites/by-domain/${window.location.hostname}`).then(() => setState("site")).catch(() => setState("app"));
  }, [state]);
  if (state === "checking") return <div className="min-h-screen" />;
  if (state === "slug") return <BrowserRouter><Routes><Route path="/site/:slug" element={<PublicSite />} /></Routes></BrowserRouter>;
  return state === "site" ? <BrowserRouter><PublicSite host={window.location.hostname} /></BrowserRouter> : children;
}

export default function App() {
  return (
    <DomainGate>
    <BrowserRouter>
      <ClerkAuthProvider>
        <MeProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/services" element={<Services />} />
            <Route path="/website-development" element={<WebsiteDevelopment />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faqs" element={<Faqs />} />
            <Route path="/legal/:page" element={<Legal />} />
            <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
            <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
            <Route path="/build" element={<Protected><Configurator /></Protected>} />
            <Route path="/build/:pid" element={<Protected><Configurator /></Protected>} />
            <Route path="/presentations" element={<Protected><PublicLayout><PresList /></PublicLayout></Protected>} />
            <Route path="/presentations/:oid" element={<Protected><PublicLayout><PresBuilder /></PublicLayout></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/dashboard/credits" element={<Protected><CreditsPage /></Protected>} />
            <Route path="/dashboard/:pid" element={<Protected><ProjectView /></Protected>} />
            <Route path="/preview/:pid" element={<Protected><PreviewPage /></Protected>} />
            <Route path="/admin" element={<Protected><StaffOnly><Admin /></StaffOnly></Protected>} />
            <Route path="/site/:slug" element={<PublicSite />} />
            <Route path="*" element={<PublicLayout><div className="max-w-xl mx-auto py-24 px-5"><h1 className="text-4xl">Page not found</h1></div></PublicLayout>} />
          </Routes>
          <Toaster richColors position="top-center" />
        </MeProvider>
      </ClerkAuthProvider>
    </BrowserRouter>
    </DomainGate>
  );
}
